import { Grid, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useProposals } from "../../services/productsService";
import TableService from "../../services/tableService";

import { useProposalTableFieldsConfig, PROPOSAL_TABLE_NAME } from "./PageTableConfig";

import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";

export default function SalesProposalPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.SALES_MANAGER, USER_ROLES.ADMIN]);

    if (!hasAccess){
        return null
    }

    const { t } = useTranslation();
    const PROPOSAL_TABLE_FIELDS_CONFIG = useProposalTableFieldsConfig();

    const { pageName } = props;
    const TABLE_CONFIGS = [{ name: PROPOSAL_TABLE_NAME, config: PROPOSAL_TABLE_FIELDS_CONFIG }];

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: proposalData, refetch, isLoading } = useProposals(searchParams);

    useEffect(() => {
        if (proposalData?.results) {
            setDataLocal(proposalData.results);
            setRecordsCount(proposalData.count);
        }
    }, [proposalData, dataPage, dataPageSize]);

    const proposalDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            PROPOSAL_TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[PROPOSAL_TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[PROPOSAL_TABLE_NAME] || [],
        );
    }, [PROPOSAL_TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                PROPOSAL_TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[PROPOSAL_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[PROPOSAL_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[PROPOSAL_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[PROPOSAL_TABLE_NAME]
                    : [],
            ),
        [
            PROPOSAL_TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
        ],
    );

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    PROPOSAL_TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[PROPOSAL_TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[PROPOSAL_TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"/products/proposals/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={proposalDataExportHeaders}
            exportToFileFileName={`${t("products.exportProposalFileName")}.xlsx`}
            showDetailsIcon={false}
            showCheckbox={false}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.proposals")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={false}
                        showMyFiltersButton={false}
                        showSearchButton={false}
                        style={{ marginBlock: "5px" }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>{tableComponent()}</LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
